import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type EarlyBirdInfo = {
  active: boolean;
  source: "event" | "series" | "global" | "none";
  ends_at?: string;
  days_before?: number;
  discount_percent?: number;
  original_price?: number;
  discounted_price?: number;
  original_credit_cost?: number;
  discounted_credit_cost?: number;
};

/**
 * Resolves the early-bird pricing for an event via the server-side
 * `get_event_early_bird` RPC. Always mirrors what booking will actually charge.
 */
export const useEventEarlyBird = (eventId: string | undefined | null) => {
  return useQuery({
    queryKey: ["event-early-bird", eventId],
    enabled: !!eventId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<EarlyBirdInfo> => {
      const { data, error } = await (supabase.rpc as any)("get_event_early_bird", {
        _event_id: eventId,
      });
      if (error) throw error;
      return (data as EarlyBirdInfo) ?? { active: false, source: "none" };
    },
  });
};

/** Human-friendly "ends in Xd Yh" string for an ends_at ISO timestamp. */
export const formatEarlyBirdCountdown = (endsAt?: string): string => {
  if (!endsAt) return "";
  const diffMs = new Date(endsAt).getTime() - Date.now();
  if (diffMs <= 0) return "ended";
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  if (days >= 2) return `${days}d left`;
  if (days === 1) return `1d ${hours}h left`;
  return `${hours}h left`;
};
